import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateProfile, completeOnboarding, isLoading } = useAuthStore();
  const [expertiseInput, setExpertiseInput] = useState((user?.expertiseAreas ?? []).join(', '));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expertiseAreas = useMemo(
    () =>
      expertiseInput
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    [expertiseInput]
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await updateProfile({ expertiseAreas });
      await completeOnboarding(3);
      navigate('/dashboard', { replace: true });
    } catch (submitError: unknown) {
      if (submitError instanceof Error) {
        setError(submitError.message);
      } else {
        setError('Unable to complete onboarding.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Welcome to beloud.pro</CardTitle>
          <CardDescription>
            Final step: tell us your focus areas so we can personalize generation and recommendations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="expertise-areas">
                Expertise areas
              </label>
              <Input
                id="expertise-areas"
                value={expertiseInput}
                onChange={(e) => setExpertiseInput(e.target.value)}
                placeholder="SaaS, Marketing, Personal Branding"
              />
              <p className="text-xs text-muted-foreground">
                Separate topics with commas. Example: Founder Stories, Product Strategy, Sales.
              </p>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing setup...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Complete onboarding
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
